import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { apiError } from '../lib/apiError';
import { formatMembershipPrice } from '../lib/membershipPrice';
import { LocaleText, useLanguage } from '../i18n/Locale';
import { LanguageSwitch } from '../i18n/LanguageSwitch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LandingPage() {
  const { user, loading, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('signin') === '1') {
      setAuthTab('login'); setAuthModalOpen(true);
    }
  }, []);
  const { language: selectedLanguage, setLanguage: handleLanguageChange } = useLanguage();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [membership, setMembership] = useState(null);
  const [membershipError, setMembershipError] = useState(false);
  const [pricingRevision, setPricingRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setMembership(null);
    setMembershipError(false);
    axios.get(`${API}/membership-pricing`, { signal: controller.signal })
      .then(response => {
        const price = formatMembershipPrice(response.data);
        if (!controller.signal.aborted) { setMembership(price); setMembershipError(price === null); }
      })
      .catch(() => { if (!controller.signal.aborted) { setMembership(null); setMembershipError(true); } });
    return () => controller.abort();
  }, [pricingRevision]);
  const joinPrice = membership || '…';
  
  // Forgot password state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    login: '',
    password: ''
  });
  
  // Registration form state
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    referral_code: ''
  });
  
  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Debug modal state changes
  useEffect(() => {
    console.log('📱 Modal state changed:', { authModalOpen, authTab });
  }, [authModalOpen, authTab]);

  // Translation system
  const translations = {
    en: {
      title: "Superior Traders Group",
      heroTitle: "Private Trading Research Group",
      heroPrefix: "Private",
      heroHighlight: "Trading",
      heroResearch: "Research",
      researchFirst: false,
      heroSuffix: "Group",
      heroSubtitle: "",
      heroDescription: "Sponsored by: Barnes Consolidated Holdings",
      loginNow: "Login Now",
      disclaimer: "Use this platform at your own risk. We do not provide trading or investment advice.",
      riskDisclosure: "Read the full Risk Disclosure",
      signIn: "Sign In"
    },
    es: {
      title: "Grupo de Traders Superiores",
      heroTitle: "Grupo Privado de Investigación de Trading",
      heroPrefix: "Grupo Privado de",
      heroHighlight: "Trading",
      heroResearch: "Investigación",
      researchFirst: true,
      heroConnector: "de",
      heroSuffix: "",
      heroSubtitle: "",
      heroDescription: "Sponsored by: Barnes Consolidated Holdings",
      loginNow: "Iniciar sesión ahora",
      disclaimer: "Utiliza esta plataforma bajo tu propia responsabilidad. No proporcionamos asesoramiento sobre trading ni inversiones.",
      riskDisclosure: "Leer la declaración completa de riesgos",
      signIn: "Iniciar Sesión"
    },
    fr: {
      title: "Groupe de Traders Supérieurs",
      heroTitle: "Groupe Privé de Recherche en Trading",
      heroPrefix: "Groupe Privé de",
      heroHighlight: "Trading",
      heroResearch: "Recherche",
      researchFirst: true,
      heroConnector: "en",
      heroSuffix: "",
      heroSubtitle: "",
      heroDescription: "Sponsored by: Barnes Consolidated Holdings",
      loginNow: "Se connecter maintenant",
      disclaimer: "Utilisez cette plateforme à vos propres risques. Nous ne fournissons aucun conseil en trading ou en investissement.",
      riskDisclosure: "Lire la déclaration complète des risques (en anglais)",
      signIn: "Se Connecter"
    }
  };

  const t = translations[selectedLanguage] || translations.en;

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  const handleLogin = async () => {
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        login: loginForm.login,
        password: loginForm.password
      });
      const sessionToken = response.data.session_token;
      if (sessionToken) {
        localStorage.setItem('session_token', sessionToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
      }
      setUser(response.data);
      setAuthModalOpen(false);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      if (error.response?.data?.detail?.code === 'approval_pending') {
        setAuthModalOpen(false); navigate('/pending');
      } else setAuthError(apiError(error, 'Sign in failed. Please check your details and try again.'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async () => {
    if (authLoading) return;
    setAuthError('');
    if (!registerForm.referral_code.trim()) { setAuthError('An admin-issued referral code is required.'); return; }
    if (!validatePassword(registerForm.password)) {
      alert('Please meet all password requirements');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      
      if (response.data.approval_required) {
        setAuthModalOpen(false); navigate('/pending');
      }
      
      // Reset form
      setRegisterForm({
        username: '',
        email: '',
        name: '',
        password: '',
        referral_code: ''
      });
      
    } catch (error) {
      setAuthError(apiError(error, 'Registration failed. Please try again.'));
    } finally {
      setAuthLoading(false);
    }
  };

  const openAuthModal = (tab = 'login') => {
    setAuthError('');
    console.log('🔄 openAuthModal called with tab:', tab);
    setAuthTab(tab);
    setAuthModalOpen(true);
    // Reset forgot password state when opening modal
    setForgotPasswordMode(false);
    setForgotPasswordEmail('');
    setForgotPasswordMessage('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setForgotPasswordMessage('Please enter your email address');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });

      setForgotPasswordMessage('If an account with that email exists, a password reset link has been sent to your inbox.');
      setForgotPasswordEmail('');
      
      // Reset to login mode after 3 seconds
      setTimeout(() => {
        setForgotPasswordMode(false);
        setForgotPasswordMessage('');
      }, 3000);

    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordMessage(
        error.response?.data?.detail || 'An error occurred. Please try again later.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Navigation */}
      <nav className="border-b border-slate-800 backdrop-blur-sm bg-slate-950/80 fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              {/* Custom Logo matching uploaded branding */}
              <div className="flex items-center space-x-2">
                {/* Candlestick Chart Icon */}
                <div className="flex items-end space-x-0.5 h-8 px-2 py-1 bg-slate-800/50 rounded-md border border-slate-700/50">
                  <div className="w-1.5 h-6 bg-green-500 rounded-sm"></div>
                  <div className="w-1.5 h-4 bg-red-500 rounded-sm"></div>
                  <div className="w-1.5 h-7 bg-green-500 rounded-sm"></div>
                  <div className="w-1.5 h-3 bg-red-500 rounded-sm"></div>
                  <div className="w-1.5 h-5 bg-green-500 rounded-sm"></div>
                </div>
                {/* Brand Text */}
                <div>
                  <div className="text-slate-100 font-bold text-sm leading-tight tracking-wide">SUPERIOR</div>
                  <div className="text-red-400 font-bold text-sm leading-tight tracking-wide">TRADERS</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language Selector */}
              <div className="flex items-center space-x-2">
                <Globe className="hidden sm:block w-4 h-4 text-slate-400" />
                <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger data-testid="homepage-language-select" className="w-[100px] min-w-[100px] sm:w-[120px] px-2 bg-slate-800 border-slate-700 text-slate-200">
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="en" data-testid="homepage-language-en" className="text-slate-200 focus:bg-slate-700">English</SelectItem>
                    <SelectItem value="es" data-testid="homepage-language-es" className="text-slate-200 focus:bg-slate-700">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={() => openAuthModal('login')} 
                className="px-2 sm:px-4 text-xs sm:text-sm bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
                data-testid="login-button"
              >
                {t.signIn}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-slate-100" data-testid="homepage-hero-title">
            {t.heroPrefix}{' '}
            {t.researchFirst && <><span className="text-red-400" data-testid="homepage-title-research">{t.heroResearch}</span>{' '}{t.heroConnector}{' '}</>}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent" data-testid="homepage-title-highlight">{t.heroHighlight}</span>
            {!t.researchFirst && <>{' '}<span className="text-red-400" data-testid="homepage-title-research">{t.heroResearch}</span></>}
            {t.heroSuffix && <> {t.heroSuffix}</>}
            {t.heroSubtitle && <><br /><span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">{t.heroSubtitle}</span></>}
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-3xl mx-auto" data-testid="homepage-sponsor">
            <LocaleText value={t.heroDescription} />
          </p>
          <Button onClick={() => openAuthModal('login')} size="lg" className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white border-0 px-8 py-6 text-lg max-w-full whitespace-normal h-auto" data-testid="hero-cta-button">
            {t.loginNow}
          </Button>
          <p className="text-xs sm:text-sm text-slate-400 mt-4 max-w-2xl mx-auto" data-testid="homepage-risk-disclaimer">{t.disclaimer}</p>
          <Link to="/risk-disclosure" data-testid="homepage-risk-disclosure-link" className="mt-2 inline-block max-w-full text-xs text-slate-300 underline underline-offset-4 transition-colors hover:text-green-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 sm:text-sm">{t.riskDisclosure}</Link>
          <div><LanguageSwitch prominent id="homepage-spanish-button" /></div>
          {membershipError && <div role="alert" className="text-amber-300 text-sm mt-4" data-testid="membership-pricing-error">Membership pricing is unavailable.<Button variant="link" onClick={() => setPricingRevision(value => value + 1)} data-testid="membership-pricing-retry">Retry</Button></div>}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4" data-testid="homepage-footer">
        <div className="max-w-7xl mx-auto text-center text-slate-500">
          <p>&copy; 2025 Superior Traders Group. All rights reserved.</p>
        </div>
      </footer>
      
      {/* Authentication Modal */}
      <Dialog open={authModalOpen} onOpenChange={setAuthModalOpen}>
        <DialogContent data-testid="authentication-dialog" closeTestId="auth-dialog-close" className="sm:max-w-md w-[calc(100%-32px)] max-h-[calc(100dvh-40px)] overflow-y-auto bg-slate-800 border-slate-700 pointer-events-auto z-50">
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {authTab === 'login' ? 'Sign In' : 'Create Account'}
            </DialogTitle>
            <DialogDescription className="sr-only">Sign in to an approved account or apply with an administrator-issued referral code.</DialogDescription>
          </DialogHeader>
          
          {authError && <p role="alert" data-testid="auth-form-error" className="text-sm text-red-300 mb-3"><LocaleText value={authError} /></p>}
          <Tabs value={authTab} onValueChange={tab => { setAuthTab(tab); setAuthError(''); }} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-900">
              <TabsTrigger data-testid="auth-login-tab" value="login" className="text-slate-300 data-[state=active]:bg-green-600 data-[state=active]:text-white">
                Sign In
              </TabsTrigger>
              <TabsTrigger data-testid="auth-register-tab" value="register" className="text-slate-300 data-[state=active]:bg-green-600 data-[state=active]:text-white">
                Register
              </TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login" className="space-y-4 mt-6">
              {!forgotPasswordMode ? (
                // Regular login form
                <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login" className="text-slate-300">Username or Email</Label>
                    <Input
                      id="login"
                      data-testid="login-username-input"
                      autoComplete="username"
                      type="text"
                      placeholder="Enter username or email"
                      value={loginForm.login}
                      onChange={(e) => setLoginForm(prev => ({ ...prev, login: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        autoComplete="current-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="login-password-visibility"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogin();
                    }}
                    disabled={authLoading || !loginForm.login || !loginForm.password}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
                    data-testid="modal-signin-button"
                  >
                    {authLoading ? 'Signing In...' : 'Sign In'}
                  </Button>
                  
                  {/* Forgot Password Link */}
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      data-testid="auth-forgot-password-link"
                      onClick={() => { setAuthError(''); setForgotPasswordMessage(''); setForgotPasswordMode(true); }}
                      className="text-sm text-blue-400 hover:text-blue-300 underline"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              ) : (
                // Forgot password form
                <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="auth-forgot-password-form">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-medium text-slate-100 mb-2">Reset Password</h3>
                    <p className="text-sm text-slate-400">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-slate-300">Email Address</Label>
                    <Input
                      id="forgot-email"
                      data-testid="auth-forgot-password-email"
                      type="email"
                      placeholder="Enter your email address"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                      required
                    />
                  </div>
                  
                  {forgotPasswordMessage && (
                    <div data-testid="auth-forgot-password-result" role="status" className={`text-sm p-3 rounded ${
                      forgotPasswordMessage.includes('sent') || forgotPasswordMessage.includes('exists')
                        ? 'bg-green-900/50 border border-green-600 text-green-300'
                        : 'bg-red-900/50 border border-red-600 text-red-300'
                    }`}>
                      <LocaleText value={forgotPasswordMessage} />
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="auth-forgot-password-back"
                      onClick={() => {
                        setForgotPasswordMode(false);
                        setForgotPasswordEmail('');
                        setForgotPasswordMessage('');
                      }}
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      Back to Login
                    </Button>
                    
                    <Button
                      type="submit"
                      disabled={!forgotPasswordEmail}
                      data-testid="auth-forgot-password-submit"
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0"
                    >
                      Send Reset Link
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register" className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Username</Label>
                <Input
                  id="username"
                  data-testid="register-username-input"
                  type="text"
                  placeholder="Choose username (3-30 chars, letters, numbers, underscore)"
                  value={registerForm.username}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="Enter email address"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Enter full name"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="register-password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create password"
                    value={registerForm.password}
                    onChange={(e) => {
                      const password = e.target.value;
                      setRegisterForm(prev => ({ ...prev, password }));
                      validatePassword(password);
                    }}
                    className="bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="register-password-visibility"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </Button>
                </div>
                
                {/* Password Requirements */}
                {registerForm.password && (
                  <div className="space-y-1 mt-2" data-testid="register-password-requirements">
                    <div data-testid="register-password-requirement-length" className={`text-xs flex items-center space-x-2 ${passwordValidation.length ? 'text-green-400' : 'text-red-400'}`}>
                      {passwordValidation.length ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>At least 8 characters</span>
                    </div>
                    <div data-testid="register-password-requirement-uppercase" className={`text-xs flex items-center space-x-2 ${passwordValidation.uppercase ? 'text-green-400' : 'text-red-400'}`}>
                      {passwordValidation.uppercase ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>One uppercase letter</span>
                    </div>
                    <div data-testid="register-password-requirement-lowercase" className={`text-xs flex items-center space-x-2 ${passwordValidation.lowercase ? 'text-green-400' : 'text-red-400'}`}>
                      {passwordValidation.lowercase ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>One lowercase letter</span>
                    </div>
                    <div data-testid="register-password-requirement-number" className={`text-xs flex items-center space-x-2 ${passwordValidation.number ? 'text-green-400' : 'text-red-400'}`}>
                      {passwordValidation.number ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>One number</span>
                    </div>
                    <div data-testid="register-password-requirement-special" className={`text-xs flex items-center space-x-2 ${passwordValidation.special ? 'text-green-400' : 'text-red-400'}`}>
                      {passwordValidation.special ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>One special character</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="register-referral-code" className="text-slate-200">Referral code <span aria-hidden="true">*</span></Label>
                <Input id="register-referral-code" data-testid="register-referral-code-input" required maxLength={80} autoComplete="off"
                  value={registerForm.referral_code} onChange={event => setRegisterForm({ ...registerForm, referral_code: event.target.value.toUpperCase() })}
                  className="bg-slate-800 border-slate-700 text-slate-100" placeholder="Code from an administrator" />
                <p className="text-xs text-slate-400" data-testid="register-referral-code-help">An active admin-issued code is required. Your account will still need approval.</p>
              </div>
              <Button
                onClick={handleRegister}
                data-testid="register-submit-button"
                disabled={authLoading || !registerForm.username || !registerForm.email || !registerForm.name || !registerForm.password || !registerForm.referral_code.trim() || !Object.values(passwordValidation).every(Boolean)}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
              >
                {authLoading ? 'Creating Account...' : 'Create Account'}
              </Button>
              
              <div className="text-xs text-slate-400 text-center" data-testid="registration-payment-notice">
                Basic accounts are free after admin approval. Premium is optional at {joinPrice}/month, renewed until canceled. Autonomous AI trading is not yet available. Payment never bypasses approval.
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LandingPage;